/*
 * ExpenseForm.tsx : Un formulaire pour ajouter ou modifier une dépense.
 */

// Import React
import React from 'react';
import {
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { Skeleton } from '@radix-ui/themes';

// Import FontAwesome
import {
	faCoins,
	faDiagramProject,
	faInfoCircle,
	faLink,
	faReceipt,
} from '@fortawesome/free-solid-svg-icons';

// Import Communs
import {
	DatePickerField,
	finishFormSave,
	FormSaveActions,
	InputField,
	SelectField,
	ToggleButtonField,
	useFormDirty,
} from '../../../common';
import { WorkflowAbortedError } from '../../../common/services/workflowClient';

// Import Layout
import CardHeader from 'layout/CardHeader';
import CardSection from 'layout/CardSection';

// Import Expense
import { ExpenseData } from '../types/types';
import { expensesApi } from '../services/expensesApi';

// Import Centre de coûts
import { costCentersApi } from '../../costCenter/services/costCentersApi';

// Import Centre de coûts - Catégories
import { costCenterCategoriesApi } from '../../costCenterCategories/services/costCenterCategoriesApi';

// Import Tiers
import { tiersApi } from '../../tiers/services/tiersApi';

// Import Utilisateurs
import { usersApi } from '../../users/services/usersApi';

// Import Contrats
import { contractsApi } from '../../contracts/services/contractsApi';

// Import Lignes de commandes
import { orderVersionsApi } from '../../orders/services/orderVersionsApi';

// Import Lignes de commandes
import { orderLineApi } from '../../orders/services/orderLineApi';

// Import Factures fournisseurs
import { supplierInvoicesApi } from '../../supplierInvoices/services/supplierInvoicesApi';
import { OrderLineData, OrderVersionData } from 'features/orders';

// Import Affaires (projects)
import { projectsApi } from 'features/projects/services/projectsApi';

// Import Éléments logistiques (pour la liaison de refacturation)
import { logisticElementsApi } from 'features/logisticElements/services/logisticElementsApi';

// Composant ExpenseForm
const ExpenseForm: React.FC = () => {
	// Hooks de Chargement
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);

	// Hooks pour l'utilisateur courant
	const userId = localStorage.getItem('userId');
	const userName = localStorage.getItem('name');
	const userFirstName = localStorage.getItem('firstName');

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const [searchParams] = useSearchParams();
	const orderLineIdParam = searchParams.get('orderLineId') || '0';
	const supplierInvoiceIdParam = searchParams.get('supplierInvoiceId') || null;
	const returnPageParam = supplierInvoiceIdParam
		? `/app/supplierInvoices/${supplierInvoiceIdParam}/edit`
		: '/app/expenses';

	// Hooks pour le formulaire courant
	const [formData, setFormData] = React.useState<ExpenseData>({
		id: 0,
		label: '',
		type: 'EXPLOITATION',
		origin: 'FREE',
		expenseDate: new Date(),
		amountHtExpected: 0.0,
		amountHtInvoiced: 0.0,
		sentToAccounting: false,
		createdAt: new Date(),
		costCenter: { id: 0, label: '' },
		supplier: { id: 0, label: '' },
		internalContact: { id: 0, name: '', firstName: '', login: '', email: '' },
		createdBy: userId
			? {
					id: Number(userId),
					name: userName || '',
					firstName: userFirstName || '',
					login: '',
					email: '',
				}
			: undefined,
		contract: undefined,
		businessOrder: undefined,
		orderLine: undefined,
	});
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Hooks select costCenter
	const [costCenterOptions, setCostCenterOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);

	// Hooks select costCenterCategory
	const [costCenterCategoryOptions, setCostCenterCategoryOptions] =
		React.useState<{ key: string; label: string }[]>([]);

	// Hooks select supplier
	const [supplierOptions, setSupplierOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingSuppliers, setLoadingSuppliers] = React.useState(true);

	// Hooks select internalContact
	const [internalContactOptions, setInternalContactOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingUsers, setLoadingUsers] = React.useState(true);

	// Hooks select contract
	const [contractOptions, setContractOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [isContractDisabled, setIsContractDisabled] = React.useState(false);
	const [loadingContracts, setLoadingContracts] = React.useState(true);

	// Hooks select order
	const [orderOptions, setOrderOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);

	// Hooks select supplierInvoice
	const [supplierInvoiceOptions, setSupplierInvoiceOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingSupplierInvoices, setLoadingSupplierInvoices] =
		React.useState(true);

	// Hooks select logisticElement (élément logistique de refacturation lié)
	const [logisticElementOptions, setLogisticElementOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingLogisticElements, setLoadingLogisticElements] =
		React.useState(true);

	// Hooks select type (statique)
	const [typeOptions] = React.useState<{ key: string; label: string }[]>([
		{ key: 'OPERATIONAL', label: 'Exploitation' },
		{ key: 'CAPITAL', label: 'Investissement' },
		{ key: 'PROJECT', label: 'Affaire' },
	]);

	// Hooks select project (affaires)
	const [projectOptions, setProjectOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingProjects, setLoadingProjects] = React.useState(true);

	// Hooks select origin (statique)
	const [originOptions] = React.useState<{ key: string; label: string }[]>([
		{ key: 'ORDER', label: 'Commande' },
		{ key: 'CONTRACT', label: 'Contrat' },
		{ key: 'FREE', label: 'Libre' },
	]);

	// Hooks select orderLine
	const [orderLineOptions, setOrderLineOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [allValidOrderLines, setAllValidOrderLines] = React.useState<
		OrderLineData[]
	>([]);
	const [isOrderLineDisabled, setIsOrderLineDisabled] = React.useState(false);

	// Hooks ligne de commande sélectionnée (données complètes pour le panneau budgétaire)
	const [selectedOrderLine, setSelectedOrderLine] =
		React.useState<OrderLineData | null>(null);

	// Hooks dépenses déjà associées à la ligne de commande sélectionnée (hors dépense courante)
	const [orderLineExpenses, setOrderLineExpenses] = React.useState<
		ExpenseData[]
	>([]);
	const [loadingOrderLineBudget, setLoadingOrderLineBudget] =
		React.useState(false);

	// Hooks select orderLine : Fonction additionelle pour construire les options des lignes de commande
	const buildOrderLineOption = (line: OrderLineData) => {
		const ov = line.orderVersion;
		const bo = ov?.businessOrder;

		const ref = bo?.referenceNumber;
		const version = ov?.versionIndex;

		const delivered = Number(line.deliveredPercent ?? 0).toLocaleString(
			'fr-FR',
			{ maximumFractionDigits: 2 }
		);

		return {
			key: String(line.id),
			label:
				(ref ? ref + ' ' : '') +
				(version !== undefined ? 'V' + version + ' → ' : '') +
				line.label +
				(line.totalExclTax ? ` (${line.totalExclTax}€)` : '') +
				` · livré ${delivered} %`,
		};
	};

	// Loading : Fetch des données et remplissage du formulaire (une fois le composant monté, si un id est présent)
	React.useEffect(() => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// On commence par charger les catégories de centres de coûts
		costCenterCategoriesApi
			.listSelect(token)
			.then((items) => {
				const options = items
					.filter((item) => item.id != null)
					.map((item) => ({
						key: item.id.toString(),
						label: String(item.label),
					}));
				setCostCenterCategoryOptions(options);
			})
			.catch((error) => console.error(error));

		// puis les lignes de commandes et les commandes
		orderLineApi
			.listPaginated(token, { limit: 999999 })
			.then((res) => {
				// 1. Déterminer la dernière orderVersion non annulée par businessOrder
				const lastVersionByOrderId: Record<number, OrderVersionData> = {};

				res.data.forEach((line) => {
					const ov = line.orderVersion;
					const bo = ov?.businessOrder;

					if (!line.id || !ov || !bo || ov.status === 'CANCELED') return;

					const current = lastVersionByOrderId[bo.id];

					if (!current || ov.versionIndex > current.versionIndex) {
						lastVersionByOrderId[bo.id] = ov;
					}
				});

				// 2. OrderLines de la dernière version uniquement
				const validLines = res.data.filter((line) => {
					const ov = line.orderVersion;
					const bo = ov?.businessOrder;

					if (!line.id || !ov || !bo) return false;

					return lastVersionByOrderId[bo.id]?.id === ov.id;
				});

				// 3. Options OrderLines
				const orderLineOptions = validLines.map(buildOrderLineOption);

				setAllValidOrderLines(validLines);
				setOrderLineOptions(orderLineOptions);

				// 4. Options Commandes (une par businessOrder)
				const orderOptions = Object.values(
					Object.entries(lastVersionByOrderId).reduce(
						(acc, [orderId, ov]) => {
							acc[orderId] = {
								key: String(orderId),
								label: ov.businessOrder?.referenceNumber || '',
							};
							return acc;
						},
						{} as Record<string, { key: string; label: string }>
					)
				);

				setOrderOptions(orderOptions);
			})
			.catch(console.error);

		// On sort la création du select des commandes afin de pouvoir liéer une dépense à une commande, même sans ligne de commande spécifique
		orderVersionsApi
			.listPaginated(token, { limit: 999999 })
			.then((res) => {
				const validVersions = res.data.filter(
					(ov) => ov.status !== 'CANCELED' && ov.businessOrder
				);
				const lastVersionByOrderId: Record<number, OrderVersionData> = {};

				validVersions.forEach((ov) => {
					const bo = ov.businessOrder;
					const current = lastVersionByOrderId[bo.id];

					if (!current || ov.versionIndex > current.versionIndex) {
						lastVersionByOrderId[bo.id] = ov;
					}
				});
				const orderOptions = Object.values(lastVersionByOrderId).map((ov) => {
					const bo = ov.businessOrder;
					return {
						key: bo && bo.id != null ? String(bo.id) : '',
						label: bo?.referenceNumber || '',
					};
				});

				setOrderOptions(orderOptions);
			})
			.catch(console.error);

		// Vérifier la présence d'un id (édition) ou non (création)
		if (id) {
			setBaselineReady(false);
			expensesApi
				.get(Number(id), token)
				.then((data) => {
					setFormData(data);
					// Si la dépense est liée à une ligne de commande, charger les données complètes de celle-ci
					// (totalSpent sur la ligne de commande exclut déjà la dépense courante côté backend)
					if (data.orderLine?.id) {
						setLoadingOrderLineBudget(true);
						orderLineApi
							.get(data.orderLine.id, token)
							.then((line) => setSelectedOrderLine(line))
							.catch((error) => {
								console.error(error);
								setLoadingOrderLineBudget(false);
							});
					}
					// filtrer les centres de coûts en utilisant directement `data`
					data.costCenter
						? costCentersApi
								.listPaginated(token, {
									sort: 'label',
									order: 'ASC',
									limit: 999999,
								})
								.then((centers) =>
									centers.data
										.filter(
											(center) =>
												center.categorie?.id === data.costCenter?.categorie?.id
										)
										.map((center) => ({
											key: center.id.toString(),
											label: center.label,
										}))
								)
								.then(setCostCenterOptions)
						: setCostCenterOptions([]);
					// Initialiser l'état des selects contract/order line en fonction de l'origine
					if (data.origin === 'CONTRACT') {
						setIsContractDisabled(false);
						setIsOrderLineDisabled(true);
					} else if (data.origin === 'ORDER') {
						setIsContractDisabled(true);
						setIsOrderLineDisabled(false);
					} else {
						setIsContractDisabled(true);
						setIsOrderLineDisabled(true);
					}
					// Préremplir les selects
					if (data.supplier) {
						setSupplierOptions([
							{
								key: data.supplier.id?.toString() || '',
								label: data.supplier.label,
							},
						]);
					}
					if (data.contract) {
						setContractOptions([
							{
								key: data.contract.id?.toString() || '',
								label: data.contract.label,
							},
						]);
					}
					if (data.supplierInvoice) {
						setSupplierInvoiceOptions([
							{
								key: data.supplierInvoice.id.toString(),
								label:
									data.supplierInvoice.referenceNumber +
									' - ' +
									data.supplierInvoice.label,
							},
						]);
					}
					if (data.logisticElement) {
						setLogisticElementOptions([
							{
								key: data.logisticElement.id.toString(),
								label: `${data.logisticElement.id} - ${data.logisticElement.label || 'Sans libellé'}`,
							},
						]);
					}
					if (data.internalContact) {
						setInternalContactOptions([
							{
								key: data.internalContact.id?.toString() || '',
								label:
									data.internalContact?.name?.toLocaleUpperCase() +
									' ' +
									data.internalContact?.firstName,
							},
						]);
					}
				})
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else if (orderLineIdParam) {
			// Si pas d'id, nouvelle dépense, on vérifie qu'une order line n'a pas été fournie
			const orderLineId = Number(orderLineIdParam);
			if (!Number.isNaN(orderLineId)) {
				orderLineApi
					.get(orderLineId, token)
					.then((data) => {
						// Fournisseur
						if (data.orderVersion?.businessOrder?.tiers) {
							const supplier = data.orderVersion.businessOrder.tiers;
							setSupplierOptions([
								{
									key: supplier.id?.toString() || '',
									label: supplier.label.toLocaleUpperCase() || '',
								},
							]);
							setFormData((prev) => ({
								...prev,
								supplier: {
									id: supplier.id,
									label: supplier.label,
								},
							}));
						}
						// Label
						if (data.label) {
							setFormData((prev) => ({
								...prev,
								label: data.label,
							}));
						}
						// Type de la dépense
						if (data.expenseType) {
							setFormData((prev) => ({
								...prev,
								type: data.expenseType,
							}));
						}
						// Centre de coûts : data.accountingCode peut correspondre à un costcenter.code. On agit alors sur le select.
						if (data.accountingCode) {
							costCentersApi
								.listPaginated(token, {
									sort: 'label',
									order: 'ASC',
									filters: {
										code: data.accountingCode,
									},
								})
								.then((res) => {
									const options = res.data.map((center) => ({
										key: center.id.toString(),
										label: center.label,
									}));
									setCostCenterOptions(options);
									// retourner les centers originaux pour conserver les méta-données (ex: categorie)
									return res.data;
								})
								.then((centers) => {
									if (centers.length > 0) {
										const center = centers[0];
										setFormData((prev) => ({
											...prev,
											costCenter: {
												id: center.id,
												label: center.label,
												categorie: center.categorie
													? {
															id: center.categorie.id,
															label: center.categorie.label,
														}
													: undefined,
											},
										}));
										// et on charge les autres centres de coûts de la même catégorie pour le select
										return costCentersApi.listPaginated(token, {
											sort: 'label',
											order: 'ASC',
											filters: {
												categorieId: center.categorie?.id?.toString() ?? '',
											},
											limit: 999999,
										});
									}
								});
						}
						// Affaire : si la ligne de commande est de type PROJECT et désigne une affaire,
						// on pré-sélectionne cette affaire dans le select du formulaire de dépense.
						if (data.expenseType === 'PROJECT' && data.project?.id) {
							const project = data.project;
							setProjectOptions([
								{
									key: project.id.toString(),
									label: project.referenceNumber,
								},
							]);
							setFormData((prev) => ({
								...prev,
								project: {
									id: project.id,
									referenceNumber: project.referenceNumber,
								},
							}));
						}
						// Montant facturé HT : pré-remplir avec le total de la ligne
						if (data.totalExclTax) {
							setFormData((prev) => ({
								...prev,
								amountHtInvoiced: data.totalExclTax,
							}));
						}
						// Stocker la ligne de commande complète pour le panneau budgétaire
						setLoadingOrderLineBudget(true);
						setSelectedOrderLine(data);
						// Origine de la dépense : forcément commande dans ce cas
						setFormData((prev) => ({
							...prev,
							origin: 'ORDER',
						}));
						setIsContractDisabled(true);
						setIsOrderLineDisabled(false);
						// Préremplir le select des lignes de commande avec la ligne sélectionnée
						setOrderLineOptions([
							{
								key: data.id.toString(),
								label:
									data.orderVersion?.businessOrder?.referenceNumber +
									' → ' +
									data.label +
									(data.totalExclTax ? ` (${data.totalExclTax}€)` : ''),
							},
						]);
						setFormData((prev) => ({
							...prev,
							orderLine: {
								id: data.id,
								label: data.label,
							},
						}));
						// et on affiche la commande dans le select des commandes
						if (data.orderVersion?.businessOrder) {
							const bo = data.orderVersion.businessOrder;

							setFormData((prev) => ({
								...prev,
								businessOrder: {
									id: bo.id,
									referenceNumber: bo.referenceNumber,
								},
							}));
						}
						// Le contact interne est celui de la order version
						if (data.orderVersion?.assignedUser) {
							const user = data.orderVersion.assignedUser;

							const label =
								(user.name || '').toLocaleUpperCase() +
								' ' +
								(user.firstName || '');

							setInternalContactOptions([
								{
									key: String(user.id), // jamais vide
									label: label,
								},
							]);

							setFormData((prev) => ({
								...prev,
								internalContact: {
									id: user.id, // doit être un nombre valide
									name: user.name || '',
									firstName: user.firstName || '',
									login: user.login || '',
									email: user.email || '',
								},
							}));
						}
						// Quand à la facture fournisseur, on l'a dans l'url de retour
						if (supplierInvoiceIdParam) {
							supplierInvoicesApi
								.get(Number(supplierInvoiceIdParam), token)
								.then((invoice) => {
									setFormData((prev) => ({
										...prev,
										supplierInvoice: {
											id: invoice.id,
											label: invoice.label,
											referenceNumber: invoice.referenceNumber,
										},
									}));
									setSupplierInvoiceOptions([
										{
											key: invoice.id.toString(),
											label: invoice.referenceNumber + ' - ' + invoice.label,
										},
									]);
								})
								.catch(console.error);
						}
					})
					.catch((error) => console.error(error))
					.finally(() => {
						setLoading(false);
						setBaselineReady(true);
					});
			}
		} else {
			// sinon, on arrête le loading directement
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	// Lazy loading : options fournisseurs en arrière-plan
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingSuppliers) {
			tiersApi
				.listSelectSuppliers(token)
				.then((items) => {
					setSupplierOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.label).toLocaleUpperCase(),
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingSuppliers(false));
		}
	}, [loading, loadingSuppliers]);

	// Lazy loading : options affaires (projects) en arrière-plan (route /select allégée)
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingProjects) {
			projectsApi
				.listSelect(token)
				.then((items) => {
					setProjectOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.referenceNumber ?? `#${item.id}`),
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingProjects(false));
		}
	}, [loading, loadingProjects]);

	// Lazy loading : options contracts en arrière-plan
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingContracts) {
			contractsApi
				.listSelect(token)
				.then((items) => {
					setContractOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.label),
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingContracts(false));
		}
	}, [loading, loadingContracts]);

	// Lazy loading : options utilisateurs en arrière-plan
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingUsers) {
			usersApi
				.listSelect(token)
				.then((items) => {
					setInternalContactOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.label),
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingUsers(false));
		}
	}, [loading, loadingUsers]);

	// Lazy loading : options factures fournisseur en arrière-plan
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingSupplierInvoices) {
			supplierInvoicesApi
				.listSelect(token)
				.then((items) => {
					setSupplierInvoiceOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.referenceNumber),
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingSupplierInvoices(false));
		}
	}, [loading, loadingSupplierInvoices]);

	// Lazy load des options d'éléments logistiques (pour la liaison de refacturation)
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingLogisticElements) {
			logisticElementsApi
				.listSelect(token)
				.then((items) => {
					setLogisticElementOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: `${item.id} - ${item.label || 'Sans libellé'}`,
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingLogisticElements(false));
		}
	}, [loading, loadingLogisticElements]);

	// Fetch des dépenses déjà associées à la ligne de commande sélectionnée (hors dépense courante)
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token || !selectedOrderLine) {
			setOrderLineExpenses([]);
			return;
		}

		let cancelled = false;
		setLoadingOrderLineBudget(true);
		expensesApi
			.listPaginated(token, {
				limit: 999999,
				filters: { 'orderLine.id': String(selectedOrderLine.id) },
			})
			.then((res) => {
				if (cancelled) return;
				// Exclure la dépense courante (mode édition) pour ne pas la compter deux fois
				const linkedExpenses = res.data.filter(
					(expense) => !id || expense.id !== Number(id)
				);
				setOrderLineExpenses(linkedExpenses);

				// En mode création uniquement : proposer par défaut le montant restant à facturer
				// (total de la ligne - somme des autres dépenses déjà associées)
				if (!id) {
					const alreadyInvoiced = linkedExpenses.reduce(
						(sum, expense) => sum + (expense.amountHtInvoiced ?? 0),
						0
					);
					const proposedAmount = Math.max(
						0,
						(selectedOrderLine.totalExclTax ?? 0) - alreadyInvoiced
					);
					setFormData((prev) => ({
						...prev,
						amountHtInvoiced: proposedAmount,
					}));
				}
			})
			.catch(console.error)
			.finally(() => {
				if (!cancelled) setLoadingOrderLineBudget(false);
			});

		return () => {
			cancelled = true;
		};
	}, [selectedOrderLine, id]);

	// Handle Formulaire : Soumission
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const onLeave = () => navigate(returnPageParam);
		// Si un id est présent, mise à jour de l'élément existant
		if (id) {
			expensesApi
				.update(Number(id), formData, token)
				.then((saved) => {
					setFormData(saved);
					finishFormSave(shouldLeave, {
						onLeave,
						markClean: () => markClean(saved),
					});
				})
				.catch((error) => {
					if (!(error instanceof WorkflowAbortedError)) console.error(error);
				})
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'un nouvel élément
			expensesApi
				.create(formData, token)
				.then((created) => {
					const search = searchParams.toString();
					finishFormSave(shouldLeave, {
						onLeave,
						markClean: () => markClean(created),
						stayOnEdit: created?.id
							? () => {
									setBaselineReady(false);
									navigate(
										`/app/expenses/${created.id}/edit${search ? `?${search}` : ''}`,
										{ replace: true }
									);
								}
							: undefined,
					});
				})
				.catch((error) => {
					if (!(error instanceof WorkflowAbortedError)) console.error(error);
				})
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	// Handle Formulaire : Changements des inputs
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Handle Formulaire : Changements des selects
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		if (name === 'costCenterCategory') {
			const selectedOption = costCenterCategoryOptions.find(
				(opt) => opt.key === value
			);
			if (!selectedOption) return;

			// Récupérer les centres de coûts filtrés par catégorie
			costCentersApi
				.listPaginated(localStorage.getItem('token') || '', {
					sort: 'label',
					order: 'ASC',
					limit: 999999,
				})
				.then((res) => {
					const options = res.data
						.filter(
							(center) => center.categorie?.id === Number(selectedOption.key)
						)
						.map((center) => ({
							key: center.id.toString(),
							label: center.label,
						}));

					setCostCenterOptions(options);

					// Sélectionner le premier centre filtré si disponible
					if (options.length > 0) {
						setFormData((prev) => ({
							...prev,
							costCenter: {
								id: Number(options[0].key),
								label: options[0].label,
								categorie: {
									id: Number(selectedOption.key),
									label: selectedOption.label,
								},
							},
						}));
					} else {
						setFormData((prev) => ({ ...prev, costCenter: null }));
					}
				})
				.catch(console.error);

			return;
		}

		if (name === 'project') {
			const selectedOption = projectOptions.find((opt) => opt.key === value);
			setFormData((prev) => ({
				...prev,
				project: selectedOption
					? {
							id: Number(selectedOption.key),
							referenceNumber: selectedOption.label,
						}
					: null,
				type: 'PROJECT',
				costCenter: null,
			}));
			return;
		}

		if (name === 'costCenter') {
			const selectedOption = costCenterOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					costCenter: {
						id: Number(selectedOption.key),
						label: selectedOption.label,
						categorie: prev.costCenter?.categorie || null,
					},
				}));
			}
			return;
		}

		if (name === 'supplier') {
			const selectedOption = supplierOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					supplier: {
						id: Number(selectedOption.key),
						label: selectedOption.label,
					},
				}));
			}
			return;
		}

		if (name === 'internalContact') {
			const selectedOption = internalContactOptions.find(
				(opt) => opt.key === value
			);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					internalContact: {
						id: Number(selectedOption.key),
						name: selectedOption.label,
						firstName: '',
						login: '',
						email: '',
					},
				}));
			}
			return;
		}

		if (name === 'contract') {
			const selectedOption = contractOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					contract: {
						id: Number(selectedOption.key),
						label: selectedOption.label,
					},
				}));
				// Si un contrat est sélectionné, on met à jour l'origine et on réinitialise la ligne de commande
				setFormData((prev) => ({
					...prev,
					origin: 'CONTRACT',
					orderLine: null,
				}));
				setIsContractDisabled(false);
				setIsOrderLineDisabled(true);
			}
			return;
		}

		if (name === 'orderLine') {
			const selectedLine = allValidOrderLines.find(
				(l) => String(l.id) === value
			);
			if (!selectedLine) return;

			// Préremplir les champs fournisseur montant facturé
			const amountHt = selectedLine.totalExclTax ?? 0;

			setFormData((prev) => ({
				...prev,
				orderLine: selectedLine,
				origin: 'ORDER',
				contract: null,
				amountHtInvoiced: amountHt,
			}));

			// Stocker la ligne complète pour le panneau budgétaire (nouvelle dépense depuis le select)
			setLoadingOrderLineBudget(true);
			setSelectedOrderLine(selectedLine);

			// Mettre à jour le select Commande
			setFormData((prev) => ({
				...prev,
				businessOrder: {
					id: Number(selectedLine.orderVersion?.businessOrder?.id),
					referenceNumber:
						selectedLine.orderVersion?.businessOrder?.referenceNumber || '',
				},
			}));

			setIsOrderLineDisabled(false);
			setIsContractDisabled(true);
			return;
		}

		if (name === 'order') {
			const orderId = Number(value);
			if (Number.isNaN(orderId)) return;

			setFormData((prev) => ({
				...prev,
				origin: 'ORDER',
				contract: null,
				orderLine: null,
				businessOrder: {
					id: orderId,
					referenceNumber: value,
				},
			}));

			setIsOrderLineDisabled(false);
			setIsContractDisabled(true);

			const filteredOptions = allValidOrderLines
				.filter((line) => line.orderVersion?.businessOrder?.id === orderId)
				.map(buildOrderLineOption);

			setOrderLineOptions(filteredOptions);
			return;
		}

		if (name === 'supplierInvoice') {
			const selectedOption = supplierInvoiceOptions.find(
				(opt) => opt.key === value
			);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					supplierInvoice: {
						id: Number(selectedOption.key),
						label: selectedOption.label,
					},
				}));
			}
			return;
		}

		if (name === 'logisticElement') {
			const selectedOption = logisticElementOptions.find(
				(opt) => opt.key === value
			);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					logisticElement: {
						id: Number(selectedOption.key),
						label: selectedOption.label,
					},
				}));
			}
			return;
		}

		if (name === 'type') {
			setFormData((prev) => ({
				...prev,
				type: value,
				// On vide les champs d'imputation selon le type sélectionné
				project: value !== 'PROJECT' ? null : prev.project,
				costCenter: value !== 'OPERATIONAL' ? null : prev.costCenter,
			}));
			return;
		}

		if (name === 'origin') {
			setFormData((prev) => ({
				...prev,
				origin: value,
			}));
			// Si value est égale à FREE, on réinitialise les champs 'contract' et 'orderLine' et on rend les deux selects disabled
			if (value === 'FREE') {
				setFormData((prev) => ({
					...prev,
					contract: null,
					orderLine: null,
				}));
				setSelectedOrderLine(null);
				setLoadingOrderLineBudget(false);
				setIsContractDisabled(true);
				setIsOrderLineDisabled(true);
			}
			// Si value est égale à 'ORDER', on réinitialise le champ 'contract' et on rend le select contract disabled
			if (value === 'ORDER') {
				setFormData((prev) => ({
					...prev,
					contract: null,
				}));
				setIsOrderLineDisabled(false);
				setIsContractDisabled(true);
			}
			// Si value est égal à 'CONTRACT', on réinitialise le champ 'orderLine'  et on rend le select orderLine disabled
			if (value === 'CONTRACT') {
				setFormData((prev) => ({
					...prev,
					orderLine: null,
				}));
				setSelectedOrderLine(null);
				setLoadingOrderLineBudget(false);
				setIsContractDisabled(false);
				setIsOrderLineDisabled(true);
			}
			return;
		}

		// Mise à jour standard pour les autres champs
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	// Handle Formulaire : Nettoyer la facture fournisseur sélectionnée
	const handleClearSupplierInvoice = () => {
		setFormData((prev) => ({
			...prev,
			supplierInvoice: null,
		}));
	};

	// Handle Formulaire : Nettoyer l'élément logistique lié
	const handleClearLogisticElement = () => {
		setFormData((prev) => ({
			...prev,
			logisticElement: null,
		}));
	};

	// Handle Panneau budgétaire : Recalcul depuis la case "montant de cette dépense" en €
	const handleThisExpenseAmountChange = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		const newAmount = parseFloat(e.target.value) || 0;
		setFormData((prev) => ({ ...prev, amountHtInvoiced: newAmount }));
	};

	// Handle Panneau budgétaire : Recalcul depuis la case "% de cette dépense"
	// → met à jour le montant de la dépense en fonction du % saisi et du total de la ligne
	const handleThisExpensePctChange = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		const newPct = parseFloat(e.target.value) || 0;
		const orderLineTotal = selectedOrderLine?.totalExclTax ?? 0;
		const newAmount = (newPct / 100) * orderLineTotal;
		setFormData((prev) => ({ ...prev, amountHtInvoiced: newAmount }));
	};

	// Handle Panneau budgétaire : Recalcul depuis la case "montant restant" en €
	// → déduit le montant de la dépense : totalLigne - déjàFacturé - restant
	const handleRemainingAmountChange = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		const newRemaining = parseFloat(e.target.value) || 0;
		const orderLineTotal = selectedOrderLine?.totalExclTax ?? 0;
		// Montant déjà facturé calculé depuis orderLineExpenses (fiable en création et édition)
		const alreadyInvoiced = orderLineExpenses.reduce(
			(sum, expense) => sum + (expense.amountHtInvoiced ?? 0),
			0
		);
		const newAmount = orderLineTotal - alreadyInvoiced - newRemaining;
		setFormData((prev) => ({
			...prev,
			amountHtInvoiced: Math.max(0, newAmount),
		}));
	};

	// Handle Panneau budgétaire : Recalcul depuis la case "% restant"
	// → convertit le % en montant restant, puis déduit le montant de la dépense
	const handleRemainingPctChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newRemainingPct = parseFloat(e.target.value) || 0;
		const orderLineTotal = selectedOrderLine?.totalExclTax ?? 0;
		// Montant déjà facturé calculé depuis orderLineExpenses (fiable en création et édition)
		const alreadyInvoiced = orderLineExpenses.reduce(
			(sum, expense) => sum + (expense.amountHtInvoiced ?? 0),
			0
		);
		const newRemaining = (newRemainingPct / 100) * orderLineTotal;
		const newAmount = orderLineTotal - alreadyInvoiced - newRemaining;
		setFormData((prev) => ({
			...prev,
			amountHtInvoiced: Math.max(0, newAmount),
		}));
	};

	// Calculs budgétaires dérivés (panneau ligne de commande)
	const orderLineTotal = selectedOrderLine?.totalExclTax ?? 0;
	// Montant déjà facturé = somme des autres dépenses liées à cette ligne (hors dépense courante),
	// calculé depuis orderLineExpenses pour être fiable en création comme en édition
	const orderLineAlreadyInvoiced = orderLineExpenses.reduce(
		(sum, expense) => sum + (expense.amountHtInvoiced ?? 0),
		0
	);
	const orderLineThisExpense = formData.amountHtInvoiced ?? 0;
	const orderLineRemaining =
		orderLineTotal - orderLineAlreadyInvoiced - orderLineThisExpense;
	// Pourcentages (protection contre la division par zéro)
	const orderLineAlreadyInvoicedPct =
		orderLineTotal > 0 ? (orderLineAlreadyInvoiced / orderLineTotal) * 100 : 0;
	const orderLineThisExpensePct =
		orderLineTotal > 0 ? (orderLineThisExpense / orderLineTotal) * 100 : 0;
	const orderLineRemainingPct =
		orderLineTotal > 0 ? (orderLineRemaining / orderLineTotal) * 100 : 0;
	const orderLineDeliveredPct = selectedOrderLine?.deliveredPercent ?? 0;
	const orderLineDeliveredAmount =
		(orderLineTotal * orderLineDeliveredPct) / 100;
	// Formatage € français
	const formatEur = (val: number) =>
		new Intl.NumberFormat('fr-FR', {
			style: 'currency',
			currency: 'EUR',
		}).format(val);

	// Render : Rendu du composant, le premier div est la card principale
	return (
		<div>
			{/* CARD PRINCIPALE */}
			<div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md space-y-4">
				{/* HEADER DE LA CARD */}
				<CardHeader
					icon={faReceipt}
					title={id ? "Édition d'une dépense" : 'Nouvelle dépense'}
					createdBy={
						formData.createdBy
							? formData.createdBy.firstName + ' ' + formData.createdBy.name
							: ''
					}
					loading={loading}
				/>

				{/* CONTENU DE LA CARD */}
				<div className="items-center space-x-4 mt-3 w-full">
					<form onSubmit={handleSubmit} className="space-y-12">
						{/* 2 COLONNES */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-16">
							{/* COLONNE HAUT GAUCHE */}
							<CardSection title="Informations générales" icon={faInfoCircle}>
								<InputField
									label="Label"
									name="label"
									value={formData.label}
									onChange={handleChange}
									required
									loading={loading}
									placeholder='ex: "Annuités d&apos;amortissements Couvercle Cuve"'
								/>
								<SelectField
									label="Fournisseur"
									selectedKey={formData.supplier?.id?.toString() || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'supplier',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={supplierOptions}
									loading={loading}
									optionsLoading={loadingSuppliers}
									entityHref={
										formData.supplier?.id
											? `/app/tiers/${formData.supplier.id}/edit`
											: undefined
									}
									onClear={() =>
										setFormData((prev) => ({
											...prev,
											supplier: null,
										}))
									}
								/>
								<DatePickerField
									label="Date de la dépense"
									name="expenseDate"
									value={
										formData.expenseDate
											? new Date(formData.expenseDate)
											: undefined
									}
									onChange={(date) =>
										setFormData({ ...formData, expenseDate: date })
									}
									required
									loading={loading}
								/>
							</CardSection>

							{/* COLONNE HAUT DROITE */}
							<CardSection
								title="Origine de la dépense"
								icon={faDiagramProject}
							>
								<SelectField
									label="Origine de la dépense"
									selectedKey={formData.origin || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'origin',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={originOptions}
									loading={loading}
								/>
								<SelectField
									label="Contrat"
									selectedKey={formData.contract?.id?.toString() || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'contract',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={contractOptions}
									loading={loading}
									optionsLoading={loadingContracts}
									readOnly={isContractDisabled}
									entityHref={
										formData.contract?.id
											? `/app/contracts/${formData.contract.id}/edit`
											: undefined
									}
									onClear={() =>
										setFormData((prev) => ({
											...prev,
											contract: null,
										}))
									}
								/>
								<SelectField
									label="Commande"
									selectedKey={formData.businessOrder?.id?.toString()}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'order',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={orderOptions}
									loading={loading}
									readOnly={isOrderLineDisabled}
									entityHref={
										formData.businessOrder?.id
											? `/app/orders/${formData.businessOrder.id}/manage`
											: undefined
									}
									onClear={() =>
										setFormData((prev) => ({
											...prev,
											businessOrder: null,
										}))
									}
								/>
								<SelectField
									label="Ligne de commande"
									selectedKey={formData.orderLine?.id?.toString() || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'orderLine',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={orderLineOptions}
									loading={loading}
									readOnly={isOrderLineDisabled}
									entityHref={
										formData.orderLine?.id
											? `/app/orderLines/${formData.orderLine.id}/edit`
											: undefined
									}
									onClear={() =>
										setFormData((prev) => ({
											...prev,
											orderLine: null,
										}))
									}
								/>
							</CardSection>
						</div>

						{/* PANNEAU BUDGÉTAIRE LIGNE DE COMMANDE : affiché dès qu'une ligne est liée, skeleton en attendant les données */}
						{formData.orderLine && (
							<CardSection
								title="Répartition budgétaire de la ligne de commande"
								icon={faCoins}
							>
								{!selectedOrderLine || loadingOrderLineBudget ? (
									<>
										<div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
											{Array.from({ length: 5 }).map((_, i) => (
												<div
													key={`budget-eur-${i}`}
													className="flex flex-col gap-1"
												>
													<Skeleton className="h-3 w-24" />
													<Skeleton className="h-9 w-full rounded" />
												</div>
											))}
										</div>
										<div className="grid grid-cols-2 md:grid-cols-5 gap-4">
											{Array.from({ length: 5 }).map((_, i) => (
												<div
													key={`budget-pct-${i}`}
													className="flex flex-col gap-1"
												>
													<Skeleton className="h-3 w-24" />
													<Skeleton className="h-9 w-full rounded" />
												</div>
											))}
										</div>
									</>
								) : (
									<>
										{/* LIGNE EN EUROS */}
										<div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
											{/* Montant livré, déduit du pourcentage – lecture seule */}
											<div className="flex flex-col gap-1">
												<label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
													Livré (€)
												</label>
												<div className="bg-gray-100 rounded px-3 py-2 text-sm font-medium text-gray-700 text-right">
													{formatEur(orderLineDeliveredAmount)}
												</div>
											</div>
											{/* Total de la ligne – lecture seule */}
											<div className="flex flex-col gap-1">
												<label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
													Total ligne (€)
												</label>
												<div className="bg-gray-100 rounded px-3 py-2 text-sm font-medium text-gray-700 text-right">
													{formatEur(orderLineTotal)}
												</div>
											</div>
											{/* Déjà facturé (autres dépenses) – lecture seule */}
											<div className="flex flex-col gap-1">
												<label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
													Déjà facturé (€)
												</label>
												<div className="bg-gray-100 rounded px-3 py-2 text-sm font-medium text-gray-700 text-right">
													{formatEur(orderLineAlreadyInvoiced)}
												</div>
											</div>
											{/* Cette dépense (€) – éditable, met à jour amountHtInvoiced */}
											<div className="flex flex-col gap-1">
												<label className="text-xs font-semibold text-blue-600 uppercase tracking-wide">
													Cette dépense (€)
												</label>
												<input
													type="number"
													step="0.01"
													value={orderLineThisExpense}
													onChange={handleThisExpenseAmountChange}
													className="border border-blue-400 rounded px-3 py-2 text-sm font-medium text-right focus:outline-none focus:ring-2 focus:ring-blue-300"
												/>
											</div>
											{/* Restant (€) – éditable, recalcule amountHtInvoiced */}
											<div className="flex flex-col gap-1">
												<label
													className={`text-xs font-semibold uppercase tracking-wide ${
														orderLineRemaining < 0
															? 'text-red-600'
															: orderLineRemaining === 0
																? 'text-green-600'
																: 'text-orange-500'
													}`}
												>
													Restant (€)
												</label>
												<input
													type="number"
													step="0.01"
													value={parseFloat(orderLineRemaining.toFixed(2))}
													onChange={handleRemainingAmountChange}
													className={`border rounded px-3 py-2 text-sm font-medium text-right focus:outline-none focus:ring-2 ${
														orderLineRemaining < 0
															? 'border-red-400 focus:ring-red-300 text-red-600'
															: orderLineRemaining === 0
																? 'border-green-400 focus:ring-green-300 text-green-700'
																: 'border-orange-400 focus:ring-orange-300 text-orange-700'
													}`}
												/>
											</div>
										</div>

										{/* LIGNE EN POURCENTAGES */}
										<div className="grid grid-cols-2 md:grid-cols-5 gap-4">
											{/* Pourcentage livré de la ligne – lecture seule */}
											<div className="flex flex-col gap-1">
												<label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
													Livré (%)
												</label>
												<div className="bg-gray-100 rounded px-3 py-2 text-sm font-medium text-gray-700 text-right">
													{Number(orderLineDeliveredPct).toLocaleString(
														'fr-FR',
														{ maximumFractionDigits: 2 }
													)}{' '}
													%
												</div>
											</div>
											{/* Total ligne (%) – toujours 100%, lecture seule */}
											<div className="flex flex-col gap-1">
												<label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
													Total ligne (%)
												</label>
												<div className="bg-gray-100 rounded px-3 py-2 text-sm font-medium text-gray-700 text-right">
													100,00 %
												</div>
											</div>
											{/* Déjà facturé (%) – lecture seule */}
											<div className="flex flex-col gap-1">
												<label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
													Déjà facturé (%)
												</label>
												<div className="bg-gray-100 rounded px-3 py-2 text-sm font-medium text-gray-700 text-right">
													{orderLineAlreadyInvoicedPct.toFixed(2)} %
												</div>
											</div>
											{/* Cette dépense (%) – éditable, recalcule amountHtInvoiced via % */}
											<div className="flex flex-col gap-1">
												<label className="text-xs font-semibold text-blue-600 uppercase tracking-wide">
													Cette dépense (%)
												</label>
												<input
													type="number"
													step="0.01"
													value={parseFloat(orderLineThisExpensePct.toFixed(4))}
													onChange={handleThisExpensePctChange}
													className="border border-blue-400 rounded px-3 py-2 text-sm font-medium text-right focus:outline-none focus:ring-2 focus:ring-blue-300"
												/>
											</div>
											{/* Restant (%) – éditable, recalcule amountHtInvoiced via % restant */}
											<div className="flex flex-col gap-1">
												<label
													className={`text-xs font-semibold uppercase tracking-wide ${
														orderLineRemaining < 0
															? 'text-red-600'
															: orderLineRemaining === 0
																? 'text-green-600'
																: 'text-orange-500'
													}`}
												>
													Restant (%)
												</label>
												<input
													type="number"
													step="0.01"
													value={parseFloat(orderLineRemainingPct.toFixed(4))}
													onChange={handleRemainingPctChange}
													className={`border rounded px-3 py-2 text-sm font-medium text-right focus:outline-none focus:ring-2 ${
														orderLineRemaining < 0
															? 'border-red-400 focus:ring-red-300 text-red-600'
															: orderLineRemaining === 0
																? 'border-green-400 focus:ring-green-300 text-green-700'
																: 'border-orange-400 focus:ring-orange-300 text-orange-700'
													}`}
												/>
											</div>
										</div>

										{/* TABLEAU DES DÉPENSES DÉJÀ ASSOCIÉES À LA LIGNE DE COMMANDE */}
										{orderLineExpenses.length > 0 && (
											<div className="mt-6">
												<p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
													Dépenses existantes sur cette ligne
												</p>
												<table className="w-full text-sm border-collapse">
													<thead>
														<tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
															<th className="text-left px-3 py-2 font-semibold">
																Libellé
															</th>
															<th className="text-left px-3 py-2 font-semibold">
																Date
															</th>
															<th className="text-left px-3 py-2 font-semibold">
																Type
															</th>
															<th className="text-left px-3 py-2 font-semibold">
																Facture
															</th>
															<th className="text-right px-3 py-2 font-semibold">
																Montant (€)
															</th>
															<th className="text-right px-3 py-2 font-semibold">
																% de la ligne
															</th>
															<th className="text-center px-3 py-2 font-semibold">
																Lien
															</th>
														</tr>
													</thead>
													<tbody>
														{orderLineExpenses.map((expense) => {
															const expenseAmount =
																expense.amountHtInvoiced ?? 0;
															const expensePct =
																orderLineTotal > 0
																	? (expenseAmount / orderLineTotal) * 100
																	: 0;
															// Libellé lisible du type de dépense
															const typeLabel =
																typeOptions.find((o) => o.key === expense.type)
																	?.label ??
																expense.type ??
																'—';
															return (
																<tr
																	key={expense.id}
																	className="border-t border-gray-100 hover:bg-gray-50"
																>
																	<td className="px-3 py-2 text-gray-700">
																		{expense.label}
																	</td>
																	<td className="px-3 py-2 text-gray-500 whitespace-nowrap">
																		{expense.expenseDate
																			? new Date(
																					expense.expenseDate
																				).toLocaleDateString('fr-FR')
																			: '—'}
																	</td>
																	<td className="px-3 py-2 text-gray-500">
																		{typeLabel}
																	</td>
																	<td className="px-3 py-2 text-gray-500">
																		{expense.supplierInvoice ? (
																			<Link
																				to={`/app/supplierInvoices/${expense.supplierInvoice.id}/edit`}
																				target="_blank"
																				rel="noopener noreferrer"
																				className="text-blue-500 hover:text-blue-700 underline text-xs"
																			>
																				{
																					expense.supplierInvoice
																						.referenceNumber
																				}
																			</Link>
																		) : (
																			'—'
																		)}
																	</td>
																	<td className="px-3 py-2 text-right text-gray-700 font-medium">
																		{formatEur(expenseAmount)}
																	</td>
																	<td className="px-3 py-2 text-right text-gray-500">
																		{expensePct.toFixed(2)} %
																	</td>
																	<td className="px-3 py-2 text-center">
																		<Link
																			to={`/app/expenses/${expense.id}/edit`}
																			target="_blank"
																			rel="noopener noreferrer"
																			className="text-blue-500 hover:text-blue-700 underline text-xs"
																		>
																			#{expense.id}
																		</Link>
																	</td>
																</tr>
															);
														})}
													</tbody>
												</table>
											</div>
										)}
									</>
								)}
							</CardSection>
						)}

						{/* 2 COLONNES */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-16">
							{/* COLONNE BAS GAUCHE */}
							<CardSection title="Type de dépense" icon={faCoins}>
								{/* Sélection du type : PROJECT, OPERATIONAL ou CAPITAL */}
								<SelectField
									label="Type de dépense"
									selectedKey={formData.type || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'type',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={typeOptions}
									loading={loading}
									required
								/>

								{/* Affaire liée — visible uniquement si type = PROJECT */}
								{formData.type === 'PROJECT' && (
									<SelectField
										label="Affaire"
										selectedKey={formData.project?.id?.toString() || ''}
										onValueChange={(val) =>
											handleChangeSelect({
												target: {
													name: 'project',
													value: val,
												},
											} as React.ChangeEvent<HTMLSelectElement>)
										}
										options={projectOptions}
										loading={loading}
										optionsLoading={loadingProjects}
										required
										entityHref={
											formData.project?.id
												? `/app/projects/${formData.project.id}/edit`
												: undefined
										}
									/>
								)}

								{/* Centre de coûts — visible uniquement si type = OPERATIONAL */}
								{formData.type === 'OPERATIONAL' && (
									<>
										<SelectField
											label="Catégorie de centre de coûts"
											selectedKey={
												formData.costCenter?.categorie?.id?.toString() || ''
											}
											onValueChange={(val) =>
												handleChangeSelect({
													target: {
														name: 'costCenterCategory',
														value: val,
													},
												} as React.ChangeEvent<HTMLSelectElement>)
											}
											options={costCenterCategoryOptions}
											loading={loading}
											required
											entityHref={
												formData.costCenter?.categorie?.id
													? `/app/settings/cost-center-categories/${formData.costCenter.categorie.id}/edit`
													: undefined
											}
										/>
										<SelectField
											label="Centre de coûts"
											selectedKey={formData.costCenter?.id?.toString() || ''}
											onValueChange={(val) =>
												handleChangeSelect({
													target: {
														name: 'costCenter',
														value: val,
													},
												} as React.ChangeEvent<HTMLSelectElement>)
											}
											options={costCenterOptions}
											loading={loading}
											required
											entityHref={
												formData.costCenter?.id
													? `/app/settings/cost-centers/${formData.costCenter.id}/edit`
													: undefined
											}
										/>
									</>
								)}

								{/* CAPITAL : pas de champ d'imputation pour l'instant */}

								<InputField
									label="Montant estimé HT"
									name="amountHtExpected"
									type="number"
									value={formData.amountHtExpected || 0}
									onChange={handleChange}
									required
									loading={loading}
									placeholder='ex: "0.0"'
									visibility={false}
								/>
								{/* Montant facturé HT : affiché seulement si aucune ligne de commande n'est liée */}
								{!formData.orderLine && (
									<InputField
										label="Montant facturé HT"
										name="amountHtInvoiced"
										type="number"
										value={formData.amountHtInvoiced || 0}
										onChange={handleChange}
										required
										loading={loading}
										placeholder='ex: "0.0"'
									/>
								)}
								<ToggleButtonField
									label="Envoyé en comptabilité"
									isOn={formData.sentToAccounting ? true : false}
									onChange={(checked) =>
										setFormData({ ...formData, sentToAccounting: checked })
									}
									loading={loading}
								/>
							</CardSection>

							{/* COLONNE BAS DROITE */}
							<CardSection title="Autres liaisons" icon={faLink}>
								<SelectField
									label="Interlocuteur interne"
									selectedKey={formData.internalContact?.id?.toString() || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'internalContact',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={internalContactOptions}
									loading={loading}
									optionsLoading={loadingUsers}
									entityHref={
										formData.internalContact?.id
											? `/app/users/${formData.internalContact.id}/edit`
											: undefined
									}
									onClear={() =>
										setFormData((prev) => ({
											...prev,
											internalContact: null,
										}))
									}
								/>
								<SelectField
									label="Facture fournisseur"
									selectedKey={formData.supplierInvoice?.id?.toString() || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'supplierInvoice',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={supplierInvoiceOptions}
									loading={loading}
									optionsLoading={loadingSupplierInvoices}
									entityHref={
										formData.supplierInvoice?.id
											? `/app/supplierInvoices/${formData.supplierInvoice.id}/edit`
											: undefined
									}
									onClear={handleClearSupplierInvoice}
								/>
								<SelectField
									label="Élément logistique de refacturation"
									selectedKey={formData.logisticElement?.id?.toString() || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'logisticElement',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={logisticElementOptions}
									loading={loading}
									optionsLoading={loadingLogisticElements}
									entityHref={
										formData.logisticElement?.id
											? `/app/logisticElements/${formData.logisticElement.id}/edit`
											: undefined
									}
									onClear={handleClearLogisticElement}
								/>
							</CardSection>
						</div>
					</form>
					<FormSaveActions
						isDirty={isDirty}
						isSubmitting={isSubmitting}
						onCancel={() => navigate(returnPageParam)}
						onSave={() => handleSave(false)}
						onSaveAndLeave={() => handleSave(true)}
					/>
				</div>
			</div>
		</div>
	);
};

export default ExpenseForm;
